/***
 *
 *   REFERRALS
 *   Who referred whom, when, and whether the referrer's reward has been
 *   credited (CORE-4074). Read-only; one row per referred signup.
 *
 **********/

import { useState, useMemo, useEffect, useRef } from 'react';
import { splitDateTime, convertToLocalTime } from '../../utility/helper';
import { Animate, useAPI, useDebounce, DataTable } from 'components/lib';
import { useNavigate } from 'react-router-dom';
import Style from './referrals.tailwind.js';
import {
  REWARD_STATUSES,
  FILTER_DEFAULTS,
  countActiveFilters,
  COLS,
  DEFAULT_SORT,
  PAGE_SIZES,
  STRINGS,
  SUBSCRIPTION_STATUS_TONE,
  subscriptionStatusLabel,
} from './referrals.constants.js';

// ─── Filter panel ─────────────────────────────────────────────────────────────

function FiltersPanel({ draft, setDraft, onApply, onClear }) {
  function pickStatus(value) {
    setDraft((prev) => ({ ...prev, status: prev.status === value ? '' : value }));
  }

  return (
    <div className={Style.filterPanel}>
      <div className={Style.filterSection}>
        <div className={Style.filterSectionLbl}>{STRINGS.rewardSectionLabel}</div>
        <div className={Style.statusChips}>
          {REWARD_STATUSES.map(({ value, label }) => {
            const isActive = draft.status === value;
            return (
              <button
                key={value}
                onClick={() => pickStatus(value)}
                className={`${Style.statusBtnBase} ${isActive ? Style.statusBtnActive : Style.statusBtnInactive}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className={Style.filterSection}>
        <div className={Style.filterSectionLbl}>{STRINGS.dateSectionLabel}</div>
        <div className={Style.dateCol}>
          <div className={Style.dateRow}>
            <span className={Style.dateFromToLabel}>{STRINGS.dateFrom}</span>
            <input
              type="date"
              aria-label={`${STRINGS.dateSectionLabel} ${STRINGS.dateFrom}`}
              value={draft.dateFrom}
              onChange={(e) => setDraft((p) => ({ ...p, dateFrom: e.target.value }))}
              className={Style.dateInput}
            />
          </div>
          <div className={Style.dateRow}>
            <span className={Style.dateFromToLabel}>{STRINGS.dateTo}</span>
            <input
              type="date"
              aria-label={`${STRINGS.dateSectionLabel} ${STRINGS.dateTo}`}
              value={draft.dateTo}
              onChange={(e) => setDraft((p) => ({ ...p, dateTo: e.target.value }))}
              className={Style.dateInput}
            />
          </div>
        </div>
      </div>

      <div className={Style.filterFooter}>
        <button onClick={onClear} className={Style.filterClearBtn}>
          {STRINGS.clearAll}
        </button>
        <button onClick={onApply} className={Style.filterApplyBtn}>
          {STRINGS.apply}
        </button>
      </div>
    </div>
  );
}

// ─── Cells ────────────────────────────────────────────────────────────────────

function openInNewTab(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function PersonCell({ colKey, id, name, email, onNavigate }) {
  if (!id) {
    return (
      <td key={colKey} className={Style.cellPerson}>
        <span className={Style.cellPersonUnknown}>{STRINGS.unknownUser}</span>
      </td>
    );
  }
  const url = `/users/${id}`;
  return (
    <td key={colKey} className={Style.cellPerson}>
      <div className={Style.cellPersonName}>
        <a
          href={url}
          onClick={(e) => onNavigate(e, url)}
          onAuxClick={(e) => {
            if (e.button !== 1) return;
            e.preventDefault();
            e.stopPropagation();
            openInNewTab(url);
          }}
          className={Style.cellPersonLink}
        >
          {name || email || '—'}
        </a>
      </div>
      {name && email && <div className={Style.cellPersonEmail}>{email}</div>}
    </td>
  );
}

function Pill({ tone, children }) {
  const toneClass =
    tone === 'good'
      ? Style.pillGood
      : tone === 'warn'
        ? Style.pillWarn
        : tone === 'bad'
          ? Style.pillBad
          : Style.pillNeutral;
  return <span className={`${Style.pillBase} ${toneClass}`}>{children}</span>;
}

function DateCell({ colKey, value, className }) {
  const { date, time } = splitDateTime(convertToLocalTime(value) || '');
  return (
    <td key={colKey} className={className}>
      <span className={Style.cellDateMain}>{date || '—'}</span>
      {time && (
        <>
          <br />
          <span className={Style.cellDateTime}>{time}</span>
        </>
      )}
    </td>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function Referrals() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(DEFAULT_SORT.key);
  const [sortDir, setSortDir] = useState(DEFAULT_SORT.dir);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [draftFilters, setDraftFilters] = useState(FILTER_DEFAULTS);
  const [appliedFilters, setAppliedFilters] = useState(FILTER_DEFAULTS);

  const debouncedSearch = useDebounce(search, 800);
  const filtersRef = useRef(null);
  const activeCount = countActiveFilters(appliedFilters);

  const referralsQuery = (() => {
    const params = new URLSearchParams();
    params.set('offset', (page - 1) * pageSize);
    params.set('limit', pageSize);
    params.set('sort', sortKey);
    params.set('order', sortDir);
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    if (appliedFilters.status) params.set('status', appliedFilters.status);
    if (appliedFilters.dateFrom) params.set('date_from', appliedFilters.dateFrom);
    if (appliedFilters.dateTo) params.set('date_to', appliedFilters.dateTo);
    return `/api/referral?${params.toString()}`;
  })();

  const referrals = useAPI(referralsQuery);
  const rows = useMemo(() => referrals.data?.results || [], [referrals.data]);
  const total = referrals.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortKey, sortDir, pageSize, appliedFilters]);

  useEffect(() => {
    if (!filtersOpen) return;
    function handler(e) {
      if (filtersRef.current && !filtersRef.current.contains(e.target)) setFiltersOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filtersOpen]);

  function openFilters() {
    setDraftFilters(appliedFilters);
    setFiltersOpen(true);
  }
  function handleApply() {
    setAppliedFilters(draftFilters);
    setFiltersOpen(false);
  }
  function handleClear() {
    setDraftFilters(FILTER_DEFAULTS);
    setAppliedFilters(FILTER_DEFAULTS);
    setFiltersOpen(false);
  }

  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === DEFAULT_SORT.key ? DEFAULT_SORT.dir : 'asc');
    }
  }

  function go(e, url) {
    e.preventDefault();
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey) openInNewTab(url);
    else navigate(url);
  }

  return (
    <Animate>
      <h1 className={Style.pageTitle}>{STRINGS.pageTitle}</h1>
      <p className={Style.pageSubtitle}>{STRINGS.pageSubtitle}</p>

      <div className={Style.topBar}>
        <div className={Style.searchWrap}>
          <span className={Style.searchIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            type="text"
            placeholder={STRINGS.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={Style.searchInput}
          />
        </div>

        <div ref={filtersRef} className={Style.filterBtnWrap}>
          <button
            onClick={() => (filtersOpen ? setFiltersOpen(false) : openFilters())}
            className={`${Style.filterBtnBase} ${activeCount > 0 ? Style.filterBtnActive : Style.filterBtnInactive}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="8" y1="12" x2="16" y2="12" />
              <line x1="11" y1="18" x2="13" y2="18" />
            </svg>
            {activeCount > 0 ? `${STRINGS.filtersBtn} · ${activeCount}` : STRINGS.filtersBtn}
          </button>

          {filtersOpen && (
            <FiltersPanel draft={draftFilters} setDraft={setDraftFilters} onApply={handleApply} onClear={handleClear} />
          )}
        </div>
      </div>

      <DataTable
        columns={COLS}
        rows={rows}
        loading={referrals.loading}
        emptyMessage={STRINGS.emptyMessage}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizes={PAGE_SIZES}
        onRowClick={(r, e) => {
          const url = `/users/${r.referee_id}`;
          if (e.ctrlKey || e.metaKey) openInNewTab(url);
          else navigate(url);
        }}
        onRowAuxClick={(r) => openInNewTab(`/users/${r.referee_id}`)}
        renderCell={(key, _value, r) => {
          if (key === 'referrer_name')
            return (
              <PersonCell
                key={key}
                colKey={key}
                id={r.referrer_id}
                name={r.referrer_name}
                email={r.referrer_email}
                onNavigate={go}
              />
            );
          if (key === 'referrer_account_name')
            return (
              <td key={key} className={Style.cellAccount}>
                {r.referrer_account_id ? (
                  <a
                    href={`/accounts/${r.referrer_account_id}`}
                    onClick={(e) => go(e, `/accounts/${r.referrer_account_id}`)}
                    onAuxClick={(e) => {
                      if (e.button !== 1) return;
                      e.preventDefault();
                      e.stopPropagation();
                      openInNewTab(`/accounts/${r.referrer_account_id}`);
                    }}
                    className={Style.cellAccountLink}
                  >
                    {r.referrer_account_name || r.referrer_account_id}
                  </a>
                ) : (
                  STRINGS.noAccount
                )}
              </td>
            );
          if (key === 'referee_name')
            return (
              <PersonCell
                key={key}
                colKey={key}
                id={r.referee_id}
                name={r.referee_name}
                email={r.referee_email}
                onNavigate={go}
              />
            );
          if (key === 'referee_subscription_status')
            return (
              <td key={key} className={Style.cellStatus}>
                <Pill tone={SUBSCRIPTION_STATUS_TONE[r.referee_subscription_status]}>
                  {subscriptionStatusLabel(r.referee_subscription_status)}
                </Pill>
              </td>
            );
          if (key === 'referral_date')
            return <DateCell key={key} colKey={key} value={r.referral_date} className={Style.cellDate} />;
          if (key === 'referral_credited_at') {
            if (!r.referral_credited_at)
              return (
                <td key={key} className={Style.cellReward}>
                  <Pill tone="neutral">{STRINGS.rewardPending}</Pill>
                </td>
              );
            const { date } = splitDateTime(convertToLocalTime(r.referral_credited_at) || '');
            return (
              <td key={key} className={Style.cellReward}>
                <Pill tone="good">{STRINGS.rewardCredited}</Pill>
                <br />
                <span className={Style.cellDateTime}>{date}</span>
              </td>
            );
          }
          return null;
        }}
      />
    </Animate>
  );
}
